import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { softImage, type SoftLook } from './soft-image.js';

/**
 * Слой с готовым размытым фоном (`soft-image.ts`).
 *
 * Холст в 128 точек браузер растягивает на слой сам — как картинку, с
 * `object-fit`. Готовый фон лежит в кэше одним экземпляром, а на экране их
 * бывает два сразу (слоты сцены при растворении), поэтому слой рисует себе
 * копию: это один `drawImage` маленькой картинки.
 */
export function SoftLayer({ image, className }: { image: HTMLCanvasElement; className?: string }): React.JSX.Element {
  const canvas = useRef<HTMLCanvasElement>(null);
  useLayoutEffect(() => {
    const target = canvas.current;
    if (target === null) return;
    target.width = image.width;
    target.height = image.height;
    target.getContext('2d')?.drawImage(image, 0, 0);
  }, [image]);
  return <canvas ref={canvas} className={className} aria-hidden="true" />;
}

/**
 * Размытая подложка по адресу картинки — там, где ждать её незачем (карточка
 * в ряду).
 *
 * `deep` — из крошечной копии: пока фон считается, стоит та же картинка,
 * растянутая как раньше (она и так расплывается); посчитался — встаёт гладкий.
 * Где холста нет, остаётся картинка.
 *
 * `light` — из крупной копии: растянутая, она была бы ЧЁТКОЙ, и подложка
 * мигнула бы резким кадром. Поэтому до готовности слоя нет вовсе — видно то,
 * что под ним.
 */
export function SoftBackdrop({
  url,
  className,
  look = 'deep',
}: {
  url: string;
  className?: string;
  look?: SoftLook;
}): React.JSX.Element | null {
  const [image, setImage] = useState<HTMLCanvasElement | null>(null);
  useEffect(() => {
    let cancelled = false;
    setImage(null);
    void softImage(url, look).then((ready) => {
      if (!cancelled) setImage(ready);
    });
    return () => {
      cancelled = true;
    };
  }, [url, look]);
  if (image !== null) return <SoftLayer image={image} className={className} />;
  if (look === 'light') return null;
  return <span className={className} style={{ backgroundImage: `url("${url}")` }} aria-hidden="true" />;
}
